import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
      <Header />
      <div className="flex-1 grid place-items-center px-6 py-16">
        <div className="w-full max-w-md">
          <Link to="/login" data-testid="forgot-back-to-login" className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#0F172A] mb-6">
            <ArrowLeft className="h-4 w-4"/> Back to sign in
          </Link>
          {sent ? (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-sm text-center" data-testid="forgot-success">
              <div className="mx-auto h-12 w-12 rounded-xl bg-emerald-100 grid place-items-center mb-4">
                <CheckCircle2 className="h-6 w-6 text-emerald-600"/>
              </div>
              <h1 className="text-2xl font-bold text-[#0F172A]">Check your inbox</h1>
              <p className="text-sm text-[#475569] mt-3 leading-relaxed">
                If an account exists for <span className="font-semibold text-[#0F172A]">{email}</span>, we've sent a password-reset link. It's valid for 30 minutes.
              </p>
              <p className="text-xs text-slate-400 mt-4">
                Didn't get it? Check your spam folder, or <button onClick={() => { setSent(false); }} className="text-[#F26953] font-semibold hover:underline">try again</button>.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight">Forgot your password?</h1>
                <p className="text-sm text-[#475569] mt-2">Enter your email and we'll send you a reset link.</p>
              </div>
              <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-[#E2E8F0] p-8 shadow-sm space-y-5" data-testid="forgot-form">
                <label className="block">
                  <span className="text-sm font-medium text-[#0F172A]">Email</span>
                  <div className="mt-1.5 relative">
                    <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                    <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      data-testid="forgot-email-input" placeholder="you@example.com"
                      className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#F26953]/20 focus:border-[#F26953] outline-none text-sm"/>
                  </div>
                </label>
                <button type="submit" disabled={submitting} data-testid="forgot-submit-btn"
                  className="w-full bg-[#F26953] text-white font-semibold py-3 rounded-lg hover:bg-[#D85340] disabled:opacity-60 transition-colors">
                  {submitting ? "Sending…" : "Send reset link"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
